import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { API_BASE_URL, API_TIMEOUT, TOKEN_STORAGE_KEY, REFRESH_TOKEN_STORAGE_KEY, TOKEN_EXPIRY_STORAGE_KEY, LAST_ACTIVITY_STORAGE_KEY } from '@/utils/constants';
import { ApiResponse, ApiError } from '@/types/auth';

export type { ApiResponse, ApiError };

class ApiClient {
  private client: AxiosInstance;
  private isRefreshing = false;
  private refreshPromise: Promise<string> | null = null;

  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      timeout: API_TIMEOUT,
      withCredentials: true, // Enable CORS credentials (cookies, auth headers)
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.setupInterceptors();
  }

  private setupInterceptors() {
    // Request interceptor to add auth token
    this.client.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem(TOKEN_STORAGE_KEY);
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }

        // Add request ID for tracking
        config.headers['X-Request-ID'] = crypto.randomUUID?.()
          ?? `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;

        return config;
      },
      (error: unknown) => {
        return Promise.reject(error instanceof Error ? error : new Error(String(error)));
      }
    );

    // Response interceptor to handle token refresh and inactivity timeout
    this.client.interceptors.response.use(
      (response) => {
        return response;
      },
      async (error) => {
        const originalRequest = error.config;

        // Handle inactivity timeout (440 status code)
        // This means the backend determined the user was inactive for too long
        if (error.response?.status === 440) {
          this.clearTokens();
          // Navigate to login with inactivity message
          // Using window.location to ensure full page navigation
          const loginUrl = new URL('/login', window.location.origin);
          loginUrl.searchParams.set('reason', 'inactivity');
          window.location.href = loginUrl.toString();
          return Promise.reject(error instanceof Error ? error : new Error(String(error)));
        }

        if (error.response?.status === 401 && !originalRequest._retry) {
          // Don't attempt token refresh for auth endpoints - they return 401
          // for invalid credentials, not expired tokens
          const authEndpoints = [
            '/auth/login',
            '/auth/register',
            '/auth/forgot-password',
            '/auth/reset-password',
            '/auth/refresh-token',
          ];
          const isAuthEndpoint = authEndpoints.some(path => originalRequest.url?.includes(path));

          if (isAuthEndpoint) {
            return Promise.reject(error instanceof Error ? error : new Error(String(error)));
          }

          if (this.isRefreshing) {
            // If we're already refreshing, wait for the new token
            try {
              const newToken = await this.refreshPromise;
              if (newToken) {
                originalRequest.headers.Authorization = `Bearer ${newToken}`;
                return this.client(originalRequest);
              }
            } catch (refreshError) {
              return Promise.reject(refreshError instanceof Error ? refreshError : new Error(String(refreshError)));
            }
          }

          originalRequest._retry = true;

          try {
            const newToken = await this.refreshToken();
            if (newToken) {
              originalRequest.headers.Authorization = `Bearer ${newToken}`;
              return this.client(originalRequest);
            }
          } catch (refreshError) {
            // Refresh failed, redirect to login
            this.clearTokens();
            window.location.href = '/login';
            return Promise.reject(refreshError instanceof Error ? refreshError : new Error(String(refreshError)));
          }
        }

        return Promise.reject(error instanceof Error ? error : new Error(String(error)));
      }
    );
  }

  private async refreshToken(): Promise<string | null> {
    if (this.isRefreshing) {
      return this.refreshPromise;
    }

    this.isRefreshing = true;
    const refreshToken = localStorage.getItem(REFRESH_TOKEN_STORAGE_KEY);

    if (!refreshToken) {
      this.isRefreshing = false;
      throw new Error('No refresh token available');
    }

    this.refreshPromise = (async () => {
      try {
        const response = await axios.post(
          `${API_BASE_URL}/api/v1/auth/refresh-token`,
          { refreshToken },
          { timeout: API_TIMEOUT }
        );

        const { accessToken } = response.data.data;
        localStorage.setItem(TOKEN_STORAGE_KEY, accessToken);
        return accessToken;
      } catch (error) {
        this.clearTokens();
        throw error;
      } finally {
        this.isRefreshing = false;
        this.refreshPromise = null;
      }
    })();

    return this.refreshPromise;
  }

  private clearTokens() {
    localStorage.removeItem(TOKEN_STORAGE_KEY);
    localStorage.removeItem(REFRESH_TOKEN_STORAGE_KEY);
    localStorage.removeItem(TOKEN_EXPIRY_STORAGE_KEY);
    localStorage.removeItem(LAST_ACTIVITY_STORAGE_KEY);
  }

  // Generic request method
  async request<T = unknown>(config: AxiosRequestConfig): Promise<ApiResponse<T>> {
    try {
      const response: AxiosResponse<ApiResponse<T>> = await this.client(config);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const apiError: ApiError = {
          code: error.response?.data?.error?.code ?? 'NETWORK_ERROR',
          message: error.response?.data?.error?.message ?? error.message,
          details: error.response?.data?.error?.details,
        };

        return {
          success: false,
          error: apiError,
          meta: {
            timestamp: new Date().toISOString(),
            requestId: (error.config?.headers?.['X-Request-ID'] as string) || 'unknown',
          },
        };
      }

      return {
        success: false,
        error: {
          code: 'UNKNOWN_ERROR',
          message: 'An unexpected error occurred',
        },
        meta: {
          timestamp: new Date().toISOString(),
          requestId: 'unknown',
        },
      };
    }
  }

  // Convenience methods
  async get<T = unknown>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    return this.request<T>({ ...config, method: 'GET', url });
  }

  async post<T = unknown>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    return this.request<T>({ ...config, method: 'POST', url, data });
  }

  async put<T = unknown>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    return this.request<T>({ ...config, method: 'PUT', url, data });
  }

  async patch<T = unknown>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    return this.request<T>({ ...config, method: 'PATCH', url, data });
  }

  async delete<T = unknown>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    return this.request<T>({ ...config, method: 'DELETE', url });
  }

  // File upload method
  async uploadFile<T = unknown>(
    url: string,
    file: File,
    onProgress?: (progress: number) => void,
    additionalData?: Record<string, unknown>
  ): Promise<ApiResponse<T>> {
    const formData = new FormData();
    formData.append('file', file);

    // Append any additional data to the form
    if (additionalData) {
      Object.entries(additionalData).forEach(([key, value]) => {
        formData.append(key, String(value));
      });
    }

    return this.request<T>({
      method: 'POST',
      url,
      data: formData,
      timeout: 120000, // 2 minutes for large file uploads
      headers: {
        'Content-Type': undefined, // Remove default application/json — browser auto-sets with boundary for FormData
      },
      onUploadProgress: (progressEvent) => {
        if (onProgress && progressEvent.total) {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onProgress(progress);
        }
      },
    });
  }

  // VERACODE-MITIGATION: CWE-80 - downloadFile creates a temporary <a> element with a blob: URL
  // from URL.createObjectURL, not user-controlled HTML. No XSS vector exists here.
  // Download file method
  async downloadFile(url: string, filename?: string): Promise<void> {
    // SSRF protection: only allow relative URLs or URLs matching our API base
    const apiBase = import.meta.env.VITE_API_BASE_URL || '';
    if (url.startsWith('http') && !url.startsWith(apiBase)) {
      throw new Error('Invalid download URL: must be a relative path or match the configured API base URL');
    }

    const response = await this.client({
      method: 'GET',
      url,
      responseType: 'blob',
      timeout: 120000, // 2 minutes for large file downloads
    });

    const contentTypeHeader = response.headers['content-type'];
    const contentType = typeof contentTypeHeader === 'string' ? contentTypeHeader : '';

    // Guard: if the server returned JSON (e.g. an error envelope that slipped
    // past the interceptor), parse it and throw instead of saving a junk file.
    if (contentType.includes('application/json') || (response.data instanceof Blob && response.data.size < 1024 && response.data.type.includes('json'))) {
      const text = response.data instanceof Blob ? await response.data.text() : JSON.stringify(response.data);
      let parsed: Record<string, unknown> = {};
      try { parsed = JSON.parse(text); } catch { /* not JSON after all — fall through */ }
      if (parsed?.success === false) {
        const errMsg = (parsed.error as Record<string, unknown> | undefined)?.message;
        throw new Error(typeof errMsg === 'string' ? errMsg : 'Download failed');
      }
    }

    // Extract filename from Content-Disposition header if available
    const contentDisposition = response.headers['content-disposition'];
    let downloadFilename = filename ?? 'download';

    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename\*?=(?:UTF-8'')?["']?([^"';\n]+)["']?/i);
      if (filenameMatch?.[1]) {
        downloadFilename = decodeURIComponent(filenameMatch[1].trim());
      }
    }

    const blob = response.data instanceof Blob
      ? response.data
      : new Blob([response.data], { type: contentType || 'application/octet-stream' });
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = downloadFilename;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    setTimeout(() => {
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    }, 1000);
  }

  // Health check method
  async healthCheck(): Promise<boolean> {
    try {
      const response = await this.get('/health');
      return response.success;
    } catch {
      return false;
    }
  }
}

// Export singleton instance
export const apiClient = new ApiClient();
export default apiClient;